import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Plus, RotateCcw, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import { fmtMoney } from "../../utils/payrollFormat";
import { toIsoDate } from "../../utils/dateFormat";
import { StatusPill } from "../Table/Table";
import IsoDateInput from "../common/Isodateinput";

import {
  addSalaryStructure,
  updateSalaryStructure,
  getStructureComponents,
  addStructureComponent,
  updateStructureComponent,
  deleteStructureComponent,
} from "../../store/slices/salaryStructureSlice";
import { fetchPayrollEntityList } from "../../store/slices/payrollSlice";
import useToast from "../../utils/useToast";
import { getEmployeeCategoryList } from "../../store/slices/employeeCategorySlice";

/* ────────────────────────────────────────────────────────────────────────── */
/* Constants                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

const INITIAL_STRUCTURE_FORM = {
  structureCode: "",
  structureName: "",
  categoryCode: "",
  effectiveDate: "",
  isActive: true,
  remarks: "",
};

const INITIAL_COMPONENT_FORM = {
  componentId: "",
  defaultAmount: "",
  defaultPercentage: "",
  sortOrder: 1,
  isRequired: true,
  isActive: true,
  remarks: "",
};

const maskCurrencyInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "");
  if (!digits) return "";

  const padded = digits.padStart(3, "0");
  const cents = padded.slice(-2);

  const wholeDigits = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const whole = Number(wholeDigits || "0").toLocaleString("en-US");

  return `${whole}.${cents}`;
};

const parseCurrency = (value) => {
  if (value === "" || value === null) return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Styles                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

const INPUT_CLASS = `
  w-full h-10
  rounded-lg
  border border-surface-200
  bg-surface-50/60
  px-3
  text-sm text-surface-800
  outline-none
  transition-all
  focus:bg-white
  focus:border-primary-500
  focus:ring-2 focus:ring-primary-500/10
`;

const DISABLED_INPUT_CLASS = `
  bg-gray-100
  text-gray-400
  border-gray-200
  opacity-60
  cursor-not-allowed
`;

const LABEL_CLASS = `
  absolute
  left-2.5
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[11px]
  font-medium
  text-surface-500
  pointer-events-none
  z-10
`;

const SECTION_TITLE_CLASS = "text-sm font-semibold text-surface-900";
const SECTION_SUBTITLE_CLASS = "mt-0.5 text-xs text-surface-500";

/* ────────────────────────────────────────────────────────────────────────── */
/* Form Fields                                                                */
/* ────────────────────────────────────────────────────────────────────────── */

function Field({ label, children, className = "", error = "" }) {
  return (
    <div className={`relative ${className}`}>
      <label className={LABEL_CLASS}>{label}</label>
      {children}
      {error && (
        <p className="mt-1 flex items-start gap-1 text-[11px] text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  placeholder = "",
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`${INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""} ${
          error ? "border-red-400 focus:border-red-500 focus:ring-red-500/10" : ""
        }`}
      />
    </Field>
  );
}

function CurrencyInput({
  label,
  value,
  onChange,
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type="text"
        inputMode="decimal"
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(maskCurrencyInput(e.target.value))}
        className={`${INPUT_CLASS} ${disabled ? DISABLED_INPUT_CLASS : ""} ${
          error ? "border-red-400 focus:border-red-500 focus:ring-red-500/10" : ""
        }`}
      />
    </Field>
  );
}

function DateInput({ label, value, onChange, disabled = false, error = "" }) {
  return (
    <Field label={label} error={error}>
      <IsoDateInput
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        className={`${INPUT_CLASS} appearance-none ${
          disabled ? DISABLED_INPUT_CLASS : ""
        } ${error ? "border-red-400 focus:border-red-500 focus:ring-red-500/10" : ""}`}
      />
    </Field>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  children,
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <select
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        className={`${INPUT_CLASS} appearance-auto cursor-pointer ${
          disabled ? DISABLED_INPUT_CLASS : ""
        } ${error ? "border-red-400 focus:border-red-500 focus:ring-red-500/10" : ""}`}
      >
        {children}
      </select>
    </Field>
  );
}

function TextArea({
  label,
  value,
  onChange,
  disabled = false,
  rows = 2,
  error = "",
  className = "",
}) {
  return (
    <Field label={label} error={error} className={className}>
      <textarea
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        rows={rows}
        className={`w-full rounded-lg border border-surface-200 bg-surface-50/60 px-3 py-2.5 text-sm text-surface-800 outline-none transition-all resize-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 ${
          disabled ? DISABLED_INPUT_CLASS : ""
        } ${error ? "border-red-400 focus:border-red-500 focus:ring-red-500/10" : ""}`}
      />
    </Field>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Main Dialog Component                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

const AddSalaryStructureDialog = ({
  open,
  onClose,
  onSuccess,
  mode = "add",
  structure = null,
}) => {
  const isAdd = mode === "add";
  const isEdit = mode === "edit";
  const isView = mode === "view";

  const dispatch = useDispatch();
  const { toast, showToast, hideToast } = useToast();

  // Redux state
  const { selectedStructureComponents = [], componentsLoading } = useSelector(
    (state) => state.salaryStructure || {}
  );
  const payrollComponents = useSelector(
    (state) => state.payroll?.entities?.payrollComponents?.list || []
  );
  const employeeCategories = useSelector(
    (state) => state.employeeCategorySlice?.categories || []
  );

  // Local state
  const [structureForm, setStructureForm] = useState(INITIAL_STRUCTURE_FORM);
  const [componentForm, setComponentForm] = useState(INITIAL_COMPONENT_FORM);
  const [showComponentForm, setShowComponentForm] = useState(false);
  const [pendingComponents, setPendingComponents] = useState([]);
  const [editingPendingIndex, setEditingPendingIndex] = useState(null);
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [componentErrors, setComponentErrors] = useState({});

  const isEditingComponent =
    showComponentForm && (Boolean(editingDetailId) || editingPendingIndex !== null);

  // Load available payroll components list if empty
  useEffect(() => {
    if (open) {
      if (payrollComponents.length === 0) {
        dispatch(fetchPayrollEntityList("payrollComponents"));
      }
      if (employeeCategories.length === 0) {
        dispatch(getEmployeeCategoryList());
      }
    }
  }, [open, dispatch, payrollComponents.length, employeeCategories.length]);

  // Reset helper
  const resetAll = useCallback(() => {
    setStructureForm(INITIAL_STRUCTURE_FORM);
    setComponentForm(INITIAL_COMPONENT_FORM);
    setPendingComponents([]);
    setShowComponentForm(false);
    setEditingPendingIndex(null);
    setEditingDetailId(null);
    setErrors({});
    setComponentErrors({});
    setSaving(false);
  }, []);

  // Initialize dialog
  useEffect(() => {
    if (!open) {
      resetAll();
      return;
    }

    setErrors({});
    setShowComponentForm(false);
    setComponentForm(INITIAL_COMPONENT_FORM);
    setPendingComponents([]);
    setEditingDetailId(null);
    setEditingPendingIndex(null);
    setComponentErrors({});

    if (structure && (isEdit || isView)) {
      setStructureForm({
        structureCode: structure.code || "",
        structureName: structure.name || "",
        categoryCode: structure.categoryCode || "",
        effectiveDate: toIsoDate(structure.effectiveDate) || "",
        isActive: structure.isActive ?? true,
        remarks: structure.remarks || "",
      });

      dispatch(getStructureComponents(structure.id));
      return;
    }

    setStructureForm({
      ...INITIAL_STRUCTURE_FORM,
      effectiveDate: toIsoDate(new Date()),
    });
  }, [open, structure, isEdit, isView, resetAll, dispatch]);

  // Keyboard shortcut (Escape)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open && !saving) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, saving, onClose]);

  /* ──────────────────────────────────────────────────────────────────────── */
  /* Structure Form Handlers                                                  */
  /* ──────────────────────────────────────────────────────────────────────── */

  const handleStructureChange = (key, value) => {
    if (isView) return;

    let finalValue = value;
    if (key === "structureCode") {
      finalValue = String(value || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5);
    }

    setStructureForm((prev) => ({
      ...prev,
      [key]: finalValue,
    }));

    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /* Component Sub-form Handlers                                              */
  /* ──────────────────────────────────────────────────────────────────────── */

  const handleComponentChange = (key, value) => {
    if (isView) return;

    setComponentForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setComponentErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const resetComponentForm = () => {
    setComponentForm(INITIAL_COMPONENT_FORM);
    setEditingDetailId(null);
    setEditingPendingIndex(null);
    setShowComponentForm(false);
    setComponentErrors({});
  };

  const getNextSortOrder = useCallback(() => {
    let maxOrder = 0;
    selectedStructureComponents.forEach((c) => {
      const order = Number(c.HSD_SORT_ORDER || 0);
      if (order > maxOrder) maxOrder = order;
    });
    pendingComponents.forEach((c) => {
      const order = Number(c.sortOrder || 0);
      if (order > maxOrder) maxOrder = order;
    });

    return maxOrder + 1;
  }, [selectedStructureComponents, pendingComponents]);

  const handleEditPendingComponent = (index) => {
    if (isView) return;
    const comp = pendingComponents[index];
    setEditingPendingIndex(index);
    setEditingDetailId(null);

    setComponentForm({
      componentId: comp.componentId ?? "",
      defaultAmount:
        comp.defaultAmount !== null && comp.defaultAmount !== ""
          ? maskCurrencyInput(String(Math.round(Number(comp.defaultAmount) * 100)))
          : "",
      defaultPercentage: comp.defaultPercentage ?? "",
      sortOrder: comp.sortOrder ?? 1,
      isRequired: comp.isRequired ?? true,
      isActive: comp.isActive ?? true,
      remarks: comp.remarks ?? "",
    });

    setShowComponentForm(true);
  };

  const handleEditExistingComponent = (comp) => {
    if (isView) return;
    setEditingDetailId(comp.HSD_STRUCTURE_DETAIL_ID);
    setEditingPendingIndex(null);

    setComponentForm({
      componentId: String(comp.HSD_COMPONENT_ID ?? ""),
      defaultAmount:
        comp.HSD_DEFAULT_AMOUNT !== null && comp.HSD_DEFAULT_AMOUNT !== ""
          ? maskCurrencyInput(String(Math.round(Number(comp.HSD_DEFAULT_AMOUNT) * 100)))
          : "",
      defaultPercentage: comp.HSD_DEFAULT_PERCENTAGE ?? "",
      sortOrder: comp.HSD_SORT_ORDER ?? 1,
      isRequired: comp.HSD_IS_REQUIRED === true || comp.HSD_IS_REQUIRED === "True",
      isActive: comp.HSD_IS_ACTIVE === true || comp.HSD_IS_ACTIVE === "True",
      remarks: comp.HSD_REMARKS ?? "",
    });

    setShowComponentForm(true);
  };

  const validateComponentSubForm = () => {
    const vErrors = {};
    if (!componentForm.componentId) {
      vErrors.componentId = "Please select a salary component.";
    }

    const duplicatePending = pendingComponents.some(
      (comp, idx) =>
        idx !== editingPendingIndex &&
        String(comp.componentId) === String(componentForm.componentId)
    );

    const duplicateSaved =
      isAdd || editingDetailId
        ? false
        : selectedStructureComponents.some(
            (comp) => String(comp.HSD_COMPONENT_ID) === String(componentForm.componentId)
          );

    if (duplicatePending || duplicateSaved) {
      vErrors.componentId = "This component has already been added.";
    }

    if (componentForm.defaultAmount !== "" && parseCurrency(componentForm.defaultAmount) < 0) {
      vErrors.defaultAmount = "Default amount cannot be negative.";
    }

    if (componentForm.defaultPercentage !== "" && Number(componentForm.defaultPercentage) < 0) {
      vErrors.defaultPercentage = "Percentage cannot be negative.";
    }

    return vErrors;
  };

  const handleAddPendingComponent = () => {
    const vErrors = validateComponentSubForm();
    if (Object.keys(vErrors).length > 0) {
      setComponentErrors(vErrors);
      return;
    }

    const formattedItem = {
      ...componentForm,
      defaultAmount:
        componentForm.defaultAmount === "" ? null : parseCurrency(componentForm.defaultAmount),
      defaultPercentage:
        componentForm.defaultPercentage === "" ? null : Number(componentForm.defaultPercentage),
      sortOrder: editingPendingIndex !== null ? Number(componentForm.sortOrder || 1) : getNextSortOrder(),
      isActive: true,
    };

    if (editingPendingIndex !== null) {
      setPendingComponents((prev) =>
        prev.map((item, idx) => (idx === editingPendingIndex ? formattedItem : item))
      );
    } else {
      setPendingComponents((prev) => [...prev, formattedItem]);
    }

    resetComponentForm();
  };

  const handleUpdateExistingComponent = async () => {
    if (!editingDetailId) return;

    const vErrors = validateComponentSubForm();
    if (Object.keys(vErrors).length > 0) {
      setComponentErrors(vErrors);
      return;
    }

    setSaving(true);
    try {
      await dispatch(
        updateStructureComponent({
          structureDetailId: editingDetailId,
          defaultAmount:
            componentForm.defaultAmount === "" ? null : parseCurrency(componentForm.defaultAmount),
          defaultPercentage:
            componentForm.defaultPercentage === "" ? null : Number(componentForm.defaultPercentage),
          sortOrder: Number(componentForm.sortOrder || 1),
          isRequired: componentForm.isRequired,
          isActive: componentForm.isActive,
          remarks: componentForm.remarks,
        })
      ).unwrap();

      showToast("Component updated successfully.", 'success');
      dispatch(getStructureComponents(structure.id));
      resetComponentForm();
    } catch (err) {
      showToast(err || "Failed to update component.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleExistingStatus = async (comp, newActiveState) => {
    setSaving(true);
    try {
      if (!newActiveState) {
        await dispatch(deleteStructureComponent(comp.HSD_STRUCTURE_DETAIL_ID)).unwrap();
        showToast("Component deactivated successfully.", "success");
      } else {
        await dispatch(
          updateStructureComponent({
            structureDetailId: comp.HSD_STRUCTURE_DETAIL_ID,
            isActive: true,
          })
        ).unwrap();
        showToast("Component reactivated successfully.", "success");
      }
      dispatch(getStructureComponents(structure.id));
    } catch (err) {
      showToast(err || "Failed to update component status.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePendingComponent = (index) => {
    setPendingComponents((prev) => prev.filter((_, idx) => idx !== index));
  };

  /* ──────────────────────────────────────────────────────────────────────── */
  /* Main Save Handler                                                        */
  /* ──────────────────────────────────────────────────────────────────────── */

  const handleSave = async () => {
    if (isView) {
      onClose?.();
      return;
    }

    if (isEditingComponent) return;

    const validationErrors = {};
    const code = structureForm.structureCode.trim();

    if (!code) {
      validationErrors.structureCode = "Structure Code is required.";
    } else if (code.length > 5) {
      validationErrors.structureCode = "Structure Code cannot be longer than 5 letters.";
    } else if (!/^[A-Z]+$/.test(code)) {
      validationErrors.structureCode = "Structure Code must consist of uppercase letters only.";
    }

    if (!structureForm.structureName.trim()) {
      validationErrors.structureName = "Structure Name is required.";
    }

    if (!structureForm.categoryCode) {
      validationErrors.categoryCode = "Please select an employee category.";
    }

    if (!structureForm.effectiveDate) {
      validationErrors.effectiveDate = "Please select an effective date.";
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    try {
      if (isAdd) {
        // 1. Create Structure
        const res = await dispatch(
          addSalaryStructure({
            structureCode: structureForm.structureCode.trim(),
            structureName: structureForm.structureName.trim(),
            categoryCode: structureForm.categoryCode,
            effectiveDate: structureForm.effectiveDate,
            isActive: structureForm.isActive,
            remarks: structureForm.remarks,
          })
        ).unwrap();

        // 2. Add pending components if any were queued
        const resultString = String(res?.Result || res?.result || "");
        const matched = resultString.match(/\d+/);
        const createdId = matched ? Number(matched[0]) : null;

        if (!createdId) {
          console.error("Failed to extract Structure ID: ", res);
        }

        if (createdId && pendingComponents.length > 0) {
          for (const comp of pendingComponents) {
            await dispatch(
              addStructureComponent({
                structureId: Number(createdId),
                componentId: Number(comp.componentId),
                defaultAmount: comp.defaultAmount,
                defaultPercentage: comp.defaultPercentage,
                sortOrder: comp.sortOrder,
                isRequired: comp.isRequired,
                isActive: comp.isActive,
                remarks: comp.remarks,
              })
            ).unwrap();
          }
        }

        showToast("Salary structure created successfully.", "success");
      } else if (isEdit) {
        // Update Structure
        await dispatch(
          updateSalaryStructure({
            structureId: structure.id,
            structureName: structureForm.structureName.trim(),
            categoryCode: structureForm.categoryCode,
            effectiveDate: structureForm.effectiveDate,
            remarks: structureForm.remarks,
          })
        ).unwrap();

        // Add any newly staged components while in edit mode
        if (pendingComponents.length > 0) {
          for (const comp of pendingComponents) {
            await dispatch(
              addStructureComponent({
                structureId: structure.id,
                componentId: Number(comp.componentId),
                defaultAmount: comp.defaultAmount,
                defaultPercentage: comp.defaultPercentage,
                sortOrder: comp.sortOrder,
                isRequired: comp.isRequired,
                isActive: comp.isActive,
                remarks: comp.remarks,
              })
            ).unwrap();
          }
        }

        showToast("Salary structure updated successfully.", "success");
      }

      onSuccess?.();
    } catch (err) {
      showToast(err || "Failed to save salary structure.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const title = isAdd
    ? "Create Salary Structure"
    : isEdit
    ? "Edit Salary Structure"
    : "Salary Structure Details";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-surface-900/40 backdrop-blur-xs transition-opacity"
        onClick={saving ? undefined : onClose}
      />

      {/* Modal Dialog Container */}
      <div className="relative z-10 flex h-[88vh] max-h-[88vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-surface-100">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">{title}</h2>
            {structure && (
              <p className="mt-0.5 text-xs text-surface-500">
                {structure.code} &bull; {structure.name}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={saving ? undefined : onClose}
            className="rounded-full p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
          {/* Section: Main Structure Details */}
          <section>
            <div className="mb-6">
              <h3 className={SECTION_TITLE_CLASS}>Structure Details</h3>
              <p className={SECTION_SUBTITLE_CLASS}>
                Define the code, name, and effective date for this salary structure.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TextInput
                label="Structure Code"
                value={structureForm.structureCode}
                disabled={!isAdd || isView}
                error={errors.structureCode}
                placeholder="e.g. BASIC"
                onChange={(e) => handleStructureChange("structureCode", e.target.value)}
              />

              <TextInput
                label="Structure Name"
                value={structureForm.structureName}
                disabled={isView}
                error={errors.structureName}
                placeholder="e.g. Basic Staff Package"
                onChange={(e) => handleStructureChange("structureName", e.target.value)}
              />

              <SelectInput
                label="Employee Category"
                value={structureForm.categoryCode}
                disabled={isView}
                error={errors.categoryCode}
                onChange={(e) => handleStructureChange("categoryCode", e.target.value)}              
              >
                <option value="">Select Employee Category</option>
                {employeeCategories.map((cat) => (
                  <option key={cat.id || cat.code} value={cat.code}>
                    {cat.name}
                  </option>
                ))}
              </SelectInput>

              <DateInput
                label="Effective Date"
                value={structureForm.effectiveDate}
                disabled={isView}
                error={errors.effectiveDate}
                onChange={(val) => handleStructureChange("effectiveDate", val)}
              />

              <TextArea
                label="Remarks"
                value={structureForm.remarks}
                disabled={isView}
                rows={1}
                onChange={(e) => handleStructureChange("remarks", e.target.value)}
              />
            </div>
          </section>

          {/* Section: Salary Components */}
          <section className="pt-2">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className={SECTION_TITLE_CLASS}>Structure Components</h3>
                <p className={SECTION_SUBTITLE_CLASS}>
                  Configure standard allowances, deductions, and base elements mapped to this structure.
                </p>
              </div>

              {!isView && (
                <button
                  type="button"
                  onClick={() => {
                    setShowComponentForm((prev) => {
                      const opening = !prev;
                      if (opening && !editingDetailId) {
                        setEditingPendingIndex(null);
                        setComponentForm({
                          ...INITIAL_COMPONENT_FORM,
                          sortOrder: getNextSortOrder(),
                        });
                      }
                      return opening;
                    });
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600/30 bg-primary-50/50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100/60"
                >
                  <Plus size={14} />
                  Add Component
                </button>
              )}
            </div>

            {/* Sub-form */}
            {showComponentForm && !isView && (
              <div className="mb-6 rounded-xl bg-surface-50/80 p-5 ring-1 ring-surface-200/70">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-surface-800">
                      {editingDetailId || editingPendingIndex !== null
                        ? "Edit Structure Component"
                        : "New Structure Component"}
                    </h4>
                    <p className="text-[11px] text-surface-500">
                      {editingDetailId
                        ? "Update the component details."
                        : "Add component allocation details."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={resetComponentForm}
                    className="rounded-full p-1 text-surface-400 hover:bg-surface-200/50 hover:text-surface-700"
                    title="Close"
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <SelectInput
                    label="Salary Component"
                    value={componentForm.componentId}
                    disabled={isView || editingDetailId}
                    error={componentErrors.componentId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      handleComponentChange("componentId", selectedId);

                      const comp = payrollComponents.find((c) => String(c.id) === String(selectedId));
                      if (comp && comp.defaultAmount > 0) {
                        handleComponentChange(
                          "defaultAmount",
                          maskCurrencyInput(Number(comp.defaultAmount).toFixed(2))
                        );
                      } else {
                        handleComponentChange("defaultAmount", "");
                      }
                    }}
                  >
                    <option value="">Select Salary Component</option>
                    {payrollComponents.map((component) => (
                      <option key={component.id} value={component.id}>
                        {component.code} - {component.name}
                      </option>
                    ))}
                  </SelectInput>

                  <CurrencyInput
                    label="Default Amount"
                    value={componentForm.defaultAmount}
                    error={componentErrors.defaultAmount}
                    onChange={(val) => handleComponentChange("defaultAmount", val)}
                  />

                  <TextInput
                    label="Sort Order"
                    type="number"
                    value={componentForm.sortOrder}
                    disabled
                    onChange={(e) => handleComponentChange("sortOrder", e.target.value)}
                  />

                  <div className="flex items-center gap-6 pt-2">
                    <label className="flex items-center gap-2 text-xs font-medium text-surface-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={componentForm.isRequired}
                        onChange={(e) => handleComponentChange("isRequired", e.target.checked)}
                        className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Is Mandatory</span>
                    </label>

                    <label className="flex items-center gap-2 text-xs font-medium text-surface-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={componentForm.isActive}
                        onChange={(e) => handleComponentChange("isActive", e.target.checked)}
                        className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Is Active</span>
                    </label>
                  </div>

                  <TextArea
                    label="Remarks"
                    value={componentForm.remarks}
                    rows={1}
                    className="sm:col-span-2"
                    onChange={(e) => handleComponentChange("remarks", e.target.value)}
                  />
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={resetComponentForm}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-surface-600 hover:bg-surface-200/50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={
                      editingDetailId ? handleUpdateExistingComponent : handleAddPendingComponent
                    }
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-xs font-medium text-white shadow-xs hover:bg-primary-700 disabled:opacity-50"
                  >
                    {!(editingDetailId || editingPendingIndex !== null) && <Plus size={13} />}
                    {editingDetailId || editingPendingIndex !== null
                      ? "Update Component"
                      : "Add Component"}
                  </button>
                </div>
              </div>
            )}

            {/* Components Table */}
            {componentsLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="animate-spin text-primary-600" size={24} />
              </div>
            ) : selectedStructureComponents.length === 0 && pendingComponents.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-xs text-surface-400">No components assigned to this structure.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-xs">
                  <thead>
                    <tr className="border-b border-surface-100 text-surface-400">
                      <th className="pb-3 text-left font-medium">Component</th>
                      <th className="pb-3 text-right font-medium">Default Amount</th>
                      <th className="pb-3 text-center font-medium">Sort Order</th>
                      <th className="pb-3 text-center font-medium">Mandatory</th>
                      <th className="pb-3 text-center font-medium">Status</th>
                      {!isView && <th className="w-20 pb-3 text-center font-medium">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100/60">
                    {/* Saved Components */}
                    {selectedStructureComponents.map((comp) => {
                      const matched = payrollComponents.find(
                        (p) => String(p.id) === String(comp.HSD_COMPONENT_ID)
                      );
                      const name = comp.HPC_COMPONENT_NAME || matched?.name || `Component ${comp.HSD_COMPONENT_ID}`;
                      const code = comp.HPC_COMPONENT_CODE || matched?.code || "";
                      const isActive = comp.HSD_IS_ACTIVE === true || comp.HSD_IS_ACTIVE === "True";
                      const isRequired = comp.HSD_IS_REQUIRED === true || comp.HSD_IS_REQUIRED === "True";

                      return (
                        <tr
                          key={`saved-${comp.HSD_STRUCTURE_DETAIL_ID}`}
                          className="group hover:bg-surface-50/50 transition-colors"
                        >
                          <td className="py-3 font-medium text-surface-800">
                            {name} {code && <span className="ml-1 text-surface-400 font-normal">({code})</span>}
                          </td>
                          <td className="py-3 text-right tabular-nums text-surface-700">
                            {comp.HSD_DEFAULT_AMOUNT != null ? fmtMoney(comp.HSD_DEFAULT_AMOUNT) : "-"}
                          </td>
                          <td className="py-3 text-center tabular-nums text-surface-500">
                            {comp.HSD_SORT_ORDER ?? 1}
                          </td>
                          <td className="py-3 text-center">
                            <span className={isRequired ? "text-primary-700 font-semibold" : "text-gray-400"}>
                              {isRequired ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="py-3 text-center">
                            <StatusPill label={isActive ? "Active" : "Inactive"} />
                          </td>
                          {!isView && (
                            <td className="py-3 text-center">
                              <div className="flex items-center justify-center gap-1 opacity-80 group-hover:opacity-100">
                                <button
                                  type="button"
                                  onClick={() => handleEditExistingComponent(comp)}
                                  disabled={saving}
                                  className="rounded p-1 text-surface-400 hover:bg-surface-100 hover:text-primary-600 disabled:opacity-50"
                                  title="Edit component"
                                >
                                  <Pencil size={13} />
                                </button>
                                {isActive ? (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleExistingStatus(comp, false)}
                                    disabled={saving}
                                    className="rounded p-1 text-surface-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                    title="Deactivate component"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleExistingStatus(comp, true)}
                                    disabled={saving}
                                    className="rounded p-1 text-surface-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
                                    title="Reactivate component"
                                  >
                                    <RotateCcw size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Pending Newly Added Components */}
                    {pendingComponents.map((comp, index) => {
                      const matched = payrollComponents.find(
                        (p) => String(p.id) === String(comp.componentId)
                      );
                      const name = matched?.name || `Component ${comp.componentId}`;
                      const code = matched?.code || "";

                      return (
                        <tr key={`pending-${index}`} className="group bg-primary-50/20">
                          <td className="py-3 font-medium text-surface-800">
                            {name} {code && <span className="ml-1 text-surface-400 font-normal">({code})</span>}
                            <span className="ml-2 rounded-full bg-primary-100/70 px-2 py-0.5 text-[9px] font-semibold text-primary-700">
                              New
                            </span>
                          </td>
                          <td className="py-3 text-right tabular-nums text-surface-700">
                            {comp.defaultAmount != null ? fmtMoney(comp.defaultAmount) : "-"}
                          </td>
                          <td className="py-3 text-center tabular-nums text-surface-500">
                            {comp.sortOrder ?? 1}
                          </td>
                          <td className="py-3 text-center">
                            <span className={comp.isRequired ? "text-primary-700 font-semibold" : "text-gray-400"}>
                              {comp.isRequired ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="py-3 text-center">
                            <StatusPill label="Active" />
                          </td>
                          <td className="py-3 text-center">
                            <div className="flex items-center justify-center gap-1 opacity-80 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() => handleEditPendingComponent(index)}
                                disabled={saving}
                                className="rounded p-1 text-surface-400 hover:bg-surface-100 hover:text-primary-600 disabled:opacity-50"
                                title="Edit component"
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemovePendingComponent(index)}
                                className="rounded p-1 text-surface-400 hover:bg-red-50 hover:text-red-500"
                                title="Remove"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-8 py-4 bg-surface-50/50 border-t border-surface-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-medium text-surface-600 hover:bg-surface-100 hover:text-surface-800 transition-colors disabled:opacity-50"
          >
            {isView ? "Close" : "Cancel"}
          </button>

          {!isView && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isEditingComponent}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white shadow-xs hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Saving..." : isEdit ? "Update" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddSalaryStructureDialog;